/**
 * The order's 5% surcharge, as an explicit /SURCHG line (kanban 51aaddc5).
 *
 * Rendered in the Order tab beside the Subtotal. Two states for the line:
 *   - default: no /SURCHG line on the order. Adam's invoice sync computes
 *     5% at BOL scan from the account's surcharge status (shown here so
 *     the desk knows what "default" will do for this account).
 *   - explicit: the order carries a /SURCHG line whose amount wins
 *     downstream — the sync copies it and appends nothing, the sales-site
 *     reports it. $0.00 means "no surcharge on this order".
 *
 * Controls: an amount input with Apply (manual), a "5%" button that takes
 * the portal's suggestion (5% of goods, minus lines on an exempt quote —
 * the base is shown so the number can be checked), "No surcharge" (explicit
 * $0.00) and "Use default" (remove the line). Drafts change locally and the
 * line rides the submission; orders already open in Sage push through
 * Edit-in-Sage. Locked (read-only) when the backend reports no edit path.
 *
 * The desk decides. Nothing here suggests whether the order should carry a
 * surcharge (owner ruling 2026-10-02: Merlin's per-order recommendation was
 * removed, kanban dde06345). Most orders never touch it, so the row is a
 * tiny "Surcharge override" disclosure, collapsed by default; the one line
 * names the explicit amount when the order carries one, and a click opens
 * the controls (owner ask on review y0jarn, 2026-10-02).
 *
 * The parent owns the order detail: after a successful save the refreshed
 * line set + totals from the response are handed back via `onChanged`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type {
  SurchargePreview,
  SurchargeSetRequest,
  SurchargeSetResponse,
} from '../../services/portalOrders';

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function toMoneyString(value: number): string {
  return value.toFixed(2);
}

function errorDetail(err: unknown, fallback: string): string {
  const detail =
    typeof err === 'object' && err !== null && 'response' in err
      ? (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      : undefined;
  return typeof detail === 'string' ? detail : fallback;
}

const muted: React.CSSProperties = { color: '#6b7280', fontSize: '12px' };
const smallBtn: React.CSSProperties = {
  background: 'none',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#374151',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const primaryBtn: React.CSSProperties = {
  ...smallBtn,
  background: '#1d4ed8',
  color: '#fff',
  border: '1px solid #1d4ed8',
};
const input: React.CSSProperties = {
  width: '96px',
  padding: '3px 6px',
  fontSize: '13px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
};

export interface OrderSurchargeControlProps {
  orderId: string;
  /** orders:manage viewer — hides every button when false. */
  canEdit: boolean;
  /** The order's line items; a change to them refreshes the preview
   *  (the 5% base follows the goods lines). */
  lineItemsKey: string;
  formatPrice: (val: string | number | null | undefined) => string;
  onChanged: (resp: SurchargeSetResponse) => void;
}

const OrderSurchargeControl: React.FC<OrderSurchargeControlProps> = ({
  orderId,
  canEdit,
  lineItemsKey,
  formatPrice,
  onChanged,
}) => {
  const [preview, setPreview] = useState<SurchargePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [amountRaw, setAmountRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [showBase, setShowBase] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    // Resolved through a promise so a service that cannot answer (an older
    // mock, a network failure) lands in the error branch, never in render.
    Promise.resolve()
      .then(() => portalOrderService.getSurcharge(orderId))
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setAmountRaw(p.current.amount != null ? toMoneyString(Number(p.current.amount)) : '');
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the surcharge.');
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, lineItemsKey]);

  const submit = useCallback(
    async (body: SurchargeSetRequest, okText: string) => {
      if (busy) return;
      setBusy(true);
      setMessage(null);
      try {
        const resp = await portalOrderService.setSurcharge(orderId, body);
        setPreview(resp.preview);
        setAmountRaw(
          resp.preview.current.amount != null
            ? toMoneyString(Number(resp.preview.current.amount))
            : '',
        );
        onChanged(resp);
        if (resp.sage_push && !resp.sage_push.success) {
          setMessage({
            kind: 'error',
            text: `Saved on the order, but the push to Sage failed: ${
              resp.sage_push.error_message ?? 'unknown error'
            }. It is flagged for retry.`,
          });
        } else {
          setMessage({
            kind: 'ok',
            text: resp.preview.edit_path === 'sage' ? `${okText} Pushed to Sage.` : okText,
          });
        }
      } catch (err: unknown) {
        setMessage({ kind: 'error', text: errorDetail(err, 'Failed to save the surcharge. Please retry.') });
      } finally {
        setBusy(false);
      }
    },
    [busy, onChanged, orderId],
  );

  const applyManual = () => {
    const n = parseAmount(amountRaw);
    if (n == null || n < 0) {
      setMessage({ kind: 'error', text: 'Enter a surcharge amount of zero or more.' });
      return;
    }
    void submit({ mode: 'manual', amount: toMoneyString(n) }, `Surcharge set to ${formatPrice(n)}.`);
  };

  const explicit = preview?.current.mode === 'explicit';
  const amount = preview?.current.amount != null ? Number(preview.current.amount) : null;
  // The one line everyone sees: the override's name, and its amount only when
  // the order actually carries one. Nothing else until it is opened.
  const summary = explicit
    ? amount === 0
      ? 'Surcharge override: none ($0.00)'
      : `Surcharge override: ${formatPrice(amount)}`
    : 'Surcharge override';

  const toggle = (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        fontSize: '11px',
        color: explicit ? '#374151' : '#9ca3af',
        fontWeight: explicit ? 600 : 400,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
      data-testid="order-surcharge-toggle"
    >
      <span aria-hidden style={{ fontSize: '9px' }}>{open ? '▾' : '▸'}</span>
      {summary}
    </button>
  );

  if (!open) {
    return (
      <div data-testid="order-surcharge-control" data-open="false">
        {toggle}
      </div>
    );
  }

  if (loadError) {
    return (
      <div data-testid="order-surcharge-control" data-open="true" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {toggle}
        <span data-testid="order-surcharge-error" style={{ ...muted, color: '#b91c1c' }}>
          {loadError}
        </span>
      </div>
    );
  }
  if (!preview) {
    return (
      <div data-testid="order-surcharge-control" data-open="true" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {toggle}
        <span data-testid="order-surcharge-loading" style={muted}>Loading…</span>
      </div>
    );
  }

  const editable = canEdit && preview.edit_path != null;
  const suggested = Number(preview.suggestion.suggested_amount);
  const account = preview.account;
  const defaultText = account
    ? account.will_bill
      ? `default — 5% added at invoicing (${account.customer_no} is not exempt)`
      : `default — none at invoicing (${account.customer_no} is exempt)`
    : 'default — decided at invoicing from the account';

  return (
    <div data-testid="order-surcharge-control" data-open="true" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {toggle}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span data-testid="order-surcharge-current" style={{ fontSize: '13px', fontWeight: 600 }}>
          {explicit
            ? amount === 0
              ? 'None (explicit $0.00)'
              : formatPrice(amount)
            : 'Not set'}
        </span>
        <span data-testid="order-surcharge-state" style={muted}>
          {explicit ? 'on the order — overrides the invoice-time surcharge' : defaultText}
        </span>
      </div>

      {editable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <input
            type="text"
            inputMode="decimal"
            value={amountRaw}
            onChange={(e) => setAmountRaw(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyManual();
            }}
            placeholder="0.00"
            aria-label="Surcharge amount"
            style={input}
            disabled={busy}
            data-testid="order-surcharge-amount"
          />
          <button
            type="button"
            onClick={applyManual}
            disabled={busy}
            style={primaryBtn}
            data-testid="order-surcharge-apply"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => void submit({ mode: 'percent' }, `Surcharge set to 5% (${formatPrice(suggested)}).`)}
            disabled={busy}
            style={smallBtn}
            title={`5% of ${formatPrice(preview.suggestion.base_total)} goods = ${formatPrice(suggested)}`}
            data-testid="order-surcharge-five-percent"
          >
            5% = {formatPrice(suggested)}
          </button>
          <button
            type="button"
            onClick={() => void submit({ mode: 'none' }, 'No surcharge on this order.')}
            disabled={busy || (explicit && amount === 0)}
            style={smallBtn}
            data-testid="order-surcharge-none"
          >
            No surcharge
          </button>
          {explicit && (
            <button
              type="button"
              onClick={() => void submit({ mode: 'default' }, 'Surcharge line removed — back to the default.')}
              disabled={busy}
              style={smallBtn}
              data-testid="order-surcharge-default"
            >
              Use default
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowBase((v) => !v)}
            style={{ ...smallBtn, border: 'none', color: '#6b7280' }}
            data-testid="order-surcharge-base-toggle"
          >
            {showBase ? 'Hide base' : 'How 5% is figured'}
          </button>
        </div>
      )}

      {!editable && canEdit && (
        <span data-testid="order-surcharge-locked" style={muted}>
          Cannot be changed from the portal for this order.
        </span>
      )}

      {showBase && (
        <div data-testid="order-surcharge-base" style={{ ...muted, lineHeight: 1.5 }}>
          Goods {formatPrice(preview.suggestion.goods_total)}
          {Number(preview.suggestion.excluded_goods_total) > 0 && (
            <>
              {' '}− {formatPrice(preview.suggestion.excluded_goods_total)} on an exempt quote (
              {preview.suggestion.excluded_lines.map((l) => l.item_code).join(', ')})
            </>
          )}
          {' '}= base {formatPrice(preview.suggestion.base_total)} × {preview.suggestion.percent}% ={' '}
          {formatPrice(preview.suggestion.suggested_amount)}
        </div>
      )}

      {message && (
        <span
          data-testid="order-surcharge-message"
          style={{ ...muted, color: message.kind === 'error' ? '#b91c1c' : '#047857' }}
        >
          {message.text}
        </span>
      )}
    </div>
  );
};

export default OrderSurchargeControl;
